import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {finalize, switchMap, take, tap} from "rxjs/operators";
import {TimeUnits} from "../../services/utils/calendar-utils";
import {Observable, of} from "rxjs";
import {V3School} from "../../model/v3/schools";
import {SchoolV3RestService} from "../../services/rest/v3/school-rest.service";
import {TransactionsV3RestService} from "../../services/rest/v3/transactions-rest.service";
import {
  IdMapping,
  MappedProductTransactionEntry,
  MappedProductWarehouseEntry,
  ProductRef,
  SupportedSubs,
} from "../../model/v3/transactions";

/**
 * catalogue definition item. Represents whole products group like "English Core"
 * or "English for Kids"
 */
export class ProductGroup{
  constructor(
    public groupName: string,
    public subGroups: ProductSubGroup[]) {
  }
}

/**
 * Products subgroup. Grouping products of the same type in the group, like
 * Exercises in English for Kids
 */
export class ProductSubGroup{
  constructor(
    public code: string,
    public name: string,
    public currency: string,
    public products: MappedProductTransactionEntry[]) {
  }
}

export interface ProductsListDataAdapter {
  syncProducts(products: ProductRef[]): Observable<any>

  listProducts(): Observable<MappedProductTransactionEntry[]>
}

@Component({
    selector: 'app-account-products',
    templateUrl: './account-products.component.html',
    styleUrls: ['./account-products.component.scss'],
    standalone: false
})
export class AccountProductsComponent implements OnInit, OnDestroy {
  // just array of number, used to iterate in the html file
  stageIndexArray: number[];
  // even indexes only (0, 2, 4...), used to iterate over paired products columns
  pairStageIndexArray: number[];
  productsCatalogue: ProductGroup[];
  // index product code to currency for faster searching
  private productCodesCurrency: {[productCode: string]: string} = {};
  private _schoolId: number;
  private schoolCredits: MappedProductWarehouseEntry[];
  schoolDetails: V3School;
  private orderLinesToCancel: MappedProductTransactionEntry[] = [];
  private productsToOrder: MappedProductTransactionEntry[] = [];
  private loading = 0;
  ownedProducts: MappedProductTransactionEntry[];
  private _productsAdapter: ProductsListDataAdapter;
  success = false;
  error = false;
  private _listName: "teacher" | "student";
  private _isTeacherSubscribed: boolean = false;
  @Output() openModalEvent = new EventEmitter<void>()

  @Input()
  hideControls = false;

  @Input()
  set schoolId(id: number) {
    this._schoolId = id;

    if (id) {
      this.loading++;
      this.loadSchoolData().pipe(
        finalize(() => this.loading-- )
      ).subscribe();
    }
  }

  @Input()
  set productList(listName: "teacher" | "student") {
    this.buildAvailableProducts(listName)
    this._listName = listName;
  }

  @Input()
  set productsAdapter(adapter: ProductsListDataAdapter) {
    this._productsAdapter = adapter
    if(adapter) this.reloadOwnedProducts().subscribe()
  }

  set isTeacherSubscribed(isSubscribed: boolean) {
    this._isTeacherSubscribed = isSubscribed
    if(this._productsAdapter)
      this.reloadOwnedProducts().subscribe()
  }

  @Output() isTeacherSubscribedChange: EventEmitter<boolean> = new EventEmitter<boolean>()

  clearSelection() {
    this.orderLinesToCancel = [];
    this.productsToOrder = [];
  }

  ngOnDestroy() {
  }

  constructor(
    private schoolV3Rest: SchoolV3RestService,
    private transactionsRest: TransactionsV3RestService,
  ) {
  }

  ngOnInit(): void {
  }

  onProductChange(subGroup: ProductSubGroup, stageIndex: number, event: Event) {
    const product = subGroup.products[stageIndex];
    const checked = (event.target as any).checked;

    if(subGroup.currency == 'subscription.BASE' && !this._isTeacherSubscribed) return;

    if (checked) {
      this.orderProduct(product);
      return;
    } else {
      this.cancelProduct(product);
    }
  }

  postOrder(): Observable<any> {
    this.success = false;
    this.error = false;
    if (!this.hasAnySelection()) return of(null)
    this.loading++;
    const productsOrderingList = this.constructProductsOrderingList();
    return this._productsAdapter.syncProducts(productsOrderingList).pipe(
      switchMap( _ => this.reloadOwnedProducts()),
      switchMap( _ => this.loadAndStoreSchoolCredits()),
      take(1),
      tap( _ => {
        this.clearSelection();
        this.showSuccess();
      }),
      finalize( () => this.loading--)
    )
  }

  submitOrder() {
    if(!this._productsAdapter) return
    this.postOrder()
    .subscribe(
      {
        error: () => {
          this.showError();
        }
      }
    )
  }

  private showSuccess() {
    this.success = true;
    setTimeout(() => this.success = false, 5000);
  }

  private showError() {
    this.error = true;
    setTimeout(() => this.error = false, 10000);
  }

  private constructProductsOrderingList() {
    const productCodesToCancel = new Set(
      this.orderLinesToCancel
        .map(line => line.product.mappedId)
    )

    const productCodes = Array.from(new Set(
      this.ownedProducts
        .map(line => line.product.mappedId)
        .filter(code => !productCodesToCancel.has(code))
        .concat(this.productsToOrder.map(line => line.product.mappedId))
    ));

    return productCodes.map(code => ({code}));
  }

  hasAnySelection() {
    return this.orderLinesToCancel.length > 0 || this.productsToOrder.length > 0;
  }

  private simpleProduct(code: string): MappedProductTransactionEntry {
    const res = this.createMappedProductTransactionEntry()
    res.product.mappedId = code
    return res
  }

  private generateProducts(codePrefix: string, count: number, codeSuffix: string = "") {
    return Array
      .from({length: count}, (_, i) => i + 1)
      .map( it => {
        const prod = this.createMappedProductTransactionEntry()
        prod.product.mappedId = `${codePrefix}${it}${codeSuffix}`;
        return prod;
      })
  }

  private createMappedProductTransactionEntry() {
    const prod = {} as MappedProductTransactionEntry;
    prod.product = {} as IdMapping
    return prod
  }

  generateTeachersProducts(codePrefix: string, count: number, codeSuffix: string = "") {
    return Array
      .from({length: count}, (_, i) => i + 1)
      .map( it => {
        if (!(it % 2)) return null;
        const prod = this.createMappedProductTransactionEntry();
        prod.product.mappedId = `${codePrefix}${it}&${it + 1}${codeSuffix}`;
        return prod;
      })
  }

  private buildAvailableProducts(listName: "teacher" | "student") {
    this.stageIndexArray = Array.from(Array(12).keys())
    this.pairStageIndexArray = this.stageIndexArray.filter( index => index % 2 == 0)

    if (listName === "teacher"){
      this.productsCatalogue = [
        new ProductGroup("Callan English", [
          new ProductSubGroup('en-books-r3', "eBooks", 'subscription.BASE', this.generateTeachersProducts("ETEBookS", 12, "r3")),
          new ProductSubGroup('en-books-r3-online', "OCC eBooks", 'subscription.BASE', this.generateTeachersProducts("ETEBookS", 4, "r3online")),
          new ProductSubGroup("en-ex-r3", "Exercises", 'casa', this.generateProducts("EExS", 12, "r3")),
          new ProductSubGroup("en-exam-r3", "Exams", 'casa', this.generateProducts("EExamS", 12, "r3")),
          new ProductSubGroup('en-transition-r3', "Transitions 1-12", "free", [this.simpleProduct("ETEBookTransr3")])
        ]),
        new ProductGroup("Teacher Guide Plus", [
          new ProductSubGroup('tgp', "eBook", 'subscription.BASE', [this.simpleProduct("TeacherGuidePlus")])
        ]),
        new ProductGroup( "Callan Español", [
          new ProductSubGroup('sp-books', "eBooks", 'casa', this.generateTeachersProducts("STEBookS", 6)),
          new ProductSubGroup('sp-ex', "Exercises", 'casa', this.generateProducts("SExS", 6)),
        ]),
        new ProductGroup( "Callan for Business", [
          new ProductSubGroup('bs-books', "eBooks", 'casa', this.generateProducts("EBTEBookS", 1)),
          new ProductSubGroup('bs-ex', "Exercises", 'casa', this.generateProducts("EBExS", 1)),
        ]),
        new ProductGroup("Callan for Kids", [
          new ProductSubGroup('kids-books', "eBooks", 'casa', this.generateTeachersProducts("EKidTEBookS", 6)),
          new ProductSubGroup('kids-ex', "Exercises", 'casa', this.generateProducts("CfkeExS", 6)),
        ]),
        new ProductGroup("Old Edition English", [
          new ProductSubGroup("en-books", "eBooks", 'casa', this.generateTeachersProducts("ETEBookS", 12)),
          new ProductSubGroup("en-ex", "Exercises", 'casa', this.generateProducts("EExS", 12))
        ]),
      ];
    } else {
      this.productsCatalogue = [
        new ProductGroup("Callan English", [
          new ProductSubGroup('en-books-r3', "eBooks", 'credit-en-r3', this.generateProducts("EEBookS", 12, "r3")),
          new ProductSubGroup("en-audio-r3", "Audio", 'casa', this.generateProducts("EABookS", 12, "r3")),
          new ProductSubGroup("en-ex-r3", "Exercises", 'casa', this.generateProducts("EExS", 12, "r3")),
          new ProductSubGroup("en-exam-r3", "Exams", 'casa', this.generateProducts("EExamS", 12, "r3")),
          new ProductSubGroup('en-transition-r3', "Transitions 1-12", "free", [this.simpleProduct("EEBookTransr3")])
        ]),
        new ProductGroup( "Callan Español", [
          new ProductSubGroup('sp-books', "eBooks", 'credit-sp', this.generateProducts("SEBookS", 6)),
          new ProductSubGroup('sp-audio', "Audio", 'casa', this.generateProducts("SABookS", 6)),
          new ProductSubGroup('sp-ex', "Exercises", 'casa', this.generateProducts("SExS", 6)),
        ]),
        new ProductGroup( "Callan for Business", [
          new ProductSubGroup('bs-books', "eBooks", 'credit-bs', this.generateProducts("EBEBookS", 1)),
          new ProductSubGroup('bs-audio', "Audio", 'casa', this.generateProducts("EBABookS", 1)),
          new ProductSubGroup('bs-ex', "Exercises", 'casa', this.generateProducts("EBExS", 1)),
        ]),
        new ProductGroup("Callan for Kids", [
          new ProductSubGroup('kids-books', "eBooks", 'credit-cfk', this.generateProducts("EKidEBookS", 6)),
          new ProductSubGroup('kids-audio', "Audio", 'casa', this.generateProducts("EKidABookS", 6)),
          new ProductSubGroup('kids-ex', "Exercises", 'casa', this.generateProducts("CfkeExS", 6)),
        ]),
        new ProductGroup("Old Edition English", [
          new ProductSubGroup("en-books", "eBooks", 'credit-en', this.generateProducts("EEBookS", 12)),
          new ProductSubGroup("en-audio", "Audio", 'casa', this.generateProducts("EABookS", 12)),
          new ProductSubGroup("en-ex", "Exercises", 'casa', this.generateProducts("EExS", 12))
        ]),
      ];
    }

    this.productsCatalogue
      .forEach( group =>
        group.subGroups.forEach( subGroup =>
          subGroup.products.forEach( product => {
              if (!product) return;
            this.productCodesCurrency[product.product.mappedId] = subGroup.currency;
            }
          )
        )
      );
  }

  private loadSchoolData() {
    return this.loadAndStoreSchoolCredits().pipe(
      switchMap( _ => this.loadAndStoreSchoolDetails())
    )
  }

  private loadAndStoreSchoolCredits() {
    this.storeSchoolCredits(null);
    return this.loadSchoolCredits().pipe(
      tap(credits => this.storeSchoolCredits(credits))
    )
  }

  private loadSchoolCredits() {
    return this.transactionsRest.getSchoolCredits(this._schoolId)
  }

  private storeSchoolCredits(credits: MappedProductWarehouseEntry[]) {
    this.schoolCredits = credits;
  }

  private loadAndStoreSchoolDetails() {
    return this.loadSchoolDetails().pipe(
      tap( schoolDetails => this.storeSchoolDetails(schoolDetails))
    )
  }

  private loadSchoolDetails() {
    return this.schoolV3Rest.getSchool(this._schoolId)
  }

  private storeSchoolDetails(schoolDetails: V3School) {
    this.schoolDetails = schoolDetails;
  }

  isSchoolPostPayAllowed() {
    if (this._listName === "teacher") return true;
    return this.hasLabel("eBooks");
  }

  private hasLabel(labelToFind: string) {
    if (!this.schoolDetails || !this.schoolDetails.labels) return false;
    return this.schoolDetails.labels.split(",")
      .map( it => it.trim())
      .find( it => it === labelToFind) != null
  }

  creditsLoaded() {
    return this.schoolCredits != null;
  }

  hasAnyCreditRow() {
    return this.schoolCredits.length > 0;
  }

  getCreditsEntriesSorted(): MappedProductWarehouseEntry[] {
    return [
      this.getCreditEntry('en-r3'),
      this.getCreditEntry('en'),
      this.getCreditEntry('sp'),
      this.getCreditEntry('bs'),
      this.getCreditEntry('cfk'),
    ].filter(it => it)
  }

  getCreditEntry(productCode: string): MappedProductWarehouseEntry {
    return this.schoolCredits.find(credit => credit.product.mappedId.endsWith(productCode))
  }

  getCreditEntryNameWithAmountText(entry: MappedProductWarehouseEntry) {
    if(!entry) return
    for ( let i = 0 ; i < this.productsCatalogue.length ; i++) {
      const group = this.productsCatalogue[i];
      if (group.subGroups.find( subGroup =>
        subGroup.currency === entry.product.mappedId)) {
        return [group.groupName, this.calculateAvailableCredits(entry.product.mappedId)]
      }
    }
    return [`Unknown credit type: ${entry.amount}`];
  }

  private calculateAvailableCredits(currency: string) {
    if (!this.creditsLoaded()) return 0;
    const creditEntry = this.schoolCredits.find( entry =>
      entry.product.mappedId === currency);
    let balance = 0;
    if (creditEntry) balance = creditEntry.amount
    balance += this.orderLinesToCancel
      .filter( line => line.currency === currency)
      .reduce((sum, next) => sum + next.itemPrice, 0)
    balance -= this.productsToOrder
      .filter( prod => this.getProductCurrency(prod) === currency)
      .length
    return balance;
  }

  private getProductCurrency(product: MappedProductTransactionEntry) {
    return this.productCodesCurrency[product.product.mappedId];
  }

  getPairStageNumber(group: ProductGroup, stageIndex: number) {
    if (this.pairedSubGroups(group).find( subGroup => this.subGroupHasStageOnPosition(subGroup, stageIndex))) {
      return `${stageIndex + 1}&${stageIndex + 2}`;
    }
    return '';
  }

  getIndividualStageNumber(group: ProductGroup, stageIndex: number) {
    if (this.individualSubGroups(group).find( subGroup => this.subGroupHasStageOnPosition(subGroup, stageIndex))) {
      return (stageIndex + 1).toString()
    }
    return '';
  }

  subGroupHasStageOnPosition(subGroup: ProductSubGroup, stageIndex: number) {
    return subGroup.products.length > stageIndex;
  }

  // paired products (like teacher eBooks 1&2, 3&4) keep null placeholders
  // on odd positions, so they are listed in a separate table with 1&2, 3&4... headers
  isPairedSubGroup(subGroup: ProductSubGroup) {
    return subGroup.products.includes(null);
  }

  pairedSubGroups(group: ProductGroup) {
    return group.subGroups.filter( subGroup => this.isPairedSubGroup(subGroup));
  }

  individualSubGroups(group: ProductGroup) {
    return group.subGroups.filter( subGroup => !this.isPairedSubGroup(subGroup));
  }

  onGroupSelectAllChange(subGroup: ProductSubGroup, $event: Event) {
    if (($event.target as any).checked) {
      this.selectAll(subGroup);
    } else {
      this.unselectAll(subGroup);
    }
  }

  isSelectAllDisabled(subGroup: ProductSubGroup) {
    for (let i = 0 ; i < subGroup.products.length ; i++) {
      if (!this.isProductDisabledByIndex(subGroup, i)) return false;
    }
    return true;
  }

  isProductDisabledByIndex(subGroup: ProductSubGroup, stageIndex: number) {
    if (this.loading) return true;
    const product = subGroup.products[stageIndex];
    if (!product) return true;

    const isChecked = this.isProductCheckedByIndex(subGroup, stageIndex);

    return (isChecked && !this.canCancel(product))
      || (!isChecked && !this.canOrder(product));
  }

  private canCancel(product: MappedProductTransactionEntry) {
    if (!product) return false;
    return this.hasProductOnOrderList(product) ||
      (this.hasProduct(product)
        && !this.hasProductOnCancelList(product)
        && this.isCancelable(product));
  }

  private canOrder(product: MappedProductTransactionEntry) {
    if (!product) return false;
    return this.hasProductOnCancelList(product) ||
      (!this.hasProduct(product)
        && !this.hasProductOnOrderList(product)
        && this.isOrderable(product));
  }

  private hasProduct(product: MappedProductTransactionEntry) {
    return (this.ownedProducts
      && this.ownedProducts.find(sp =>
        sp.product.mappedId === product.product.mappedId) != null);
  }

  private isCancelable(product: MappedProductTransactionEntry) {
    if (!product) return false;
    const lineToCancel = this.ownedProducts.find(sp =>
      sp.product.mappedId === product.product.mappedId);

    if (!lineToCancel) return false;
    if (lineToCancel.currency === "casa") return true;
    return (new Date(lineToCancel.cancelExpiryDate)).getTime()  >
      new Date().getTime() + TimeUnits.Minues(10).toMilis();
  }

  private isOrderable(product: MappedProductTransactionEntry) {
    if (this.isSchoolPostPayAllowed()) return true;
    const currency = this.getProductCurrency(product);
    if ( currency === "casa" || currency === "free") return true;
    const credits = this.calculateAvailableCredits(currency);
    return credits > 0;
  }

  private hasProductOnCancelList(product: MappedProductTransactionEntry) {
    return this.orderLinesToCancel.find( line =>
      line.product.mappedId === product.product.mappedId) != null
  }

  private hasProductOnOrderList(product: MappedProductTransactionEntry) {
    if (!product) return false;
    return this.productsToOrder.find( toOrder =>
      toOrder.product.mappedId === product.product.mappedId) != null;
  }

  isProductCheckedByIndex(subGroup: ProductSubGroup, stageIndex: number) {
    const product = subGroup.products[stageIndex];
    if (!product) return false;
    return (this.hasProduct(product) || this.hasProductOnOrderList(product)) && !this.hasProductOnCancelList(product);
  }

  isSelectAllChecked(subGroup: ProductSubGroup) {
    if (this.isSelectAllDisabled(subGroup)) return false;

    for (let i = 0 ; i < subGroup.products.length ; i++) {
      if (!this.isProductDisabledByIndex(subGroup, i)
        && !this.isProductCheckedByIndex(subGroup, i)
      ) {
        return false;
      }
    }
    return true;
  }

  private selectAll(subGroup: ProductSubGroup) {
    for (let i = 0 ; i < subGroup.products.length ; i++) {
      if (this.isProductDisabledByIndex(subGroup, i)
        || this.isProductCheckedByIndex(subGroup, i)) continue;
      this.orderProduct(subGroup.products[i]);
    }
  }

  private unselectAll(subGroup: ProductSubGroup) {
    for (let i = 0 ; i < subGroup.products.length ; i++ ) {
      if (this.isProductDisabledByIndex(subGroup, i)
        || !this.isProductCheckedByIndex(subGroup, i)) continue;
      this.cancelProduct(subGroup.products[i]);
    }
  }

  orderProduct(product: MappedProductTransactionEntry) {
    if (!this.canOrder(product)) return;

    // bring back canceled product
    const cancellingPos = this.orderLinesToCancel.findIndex( line =>
      line.product.mappedId === product.product.mappedId
    );

    if (cancellingPos>=0) {
      this.orderLinesToCancel.splice(cancellingPos, 1);
    } else {
      this.productsToOrder.push(product);
    }
  }

  cancelProduct(product: MappedProductTransactionEntry) {
    if (!this.canCancel(product)) return;

    const orderingPos = this.productsToOrder.findIndex( orderingLine =>
      orderingLine.product.mappedId === product.product.mappedId
    );

    if (orderingPos >= 0) {
      this.productsToOrder.splice(orderingPos, 1);
      return;
    }
    // or add to canceling
    const lineToCancel = this.ownedProducts.find(sp =>
      sp.product.mappedId === product.product.mappedId)
    if (!lineToCancel) return;
    this.orderLinesToCancel.push(lineToCancel);
  }

  private reloadOwnedProducts() {
    this.loading++;
    return this._productsAdapter.listProducts()
    .pipe(
      tap(products => this.storeProducts(products)),
      tap(entries => this.checkSubscriptionStatus(entries)),
      finalize( () => this.loading-- )
    )
  }

  private storeProducts(products: MappedProductTransactionEntry[]) {
    this.ownedProducts = products;
  }

  isLoading() {
    return this.loading > 0;
  }

  hasProductCredits(subGroup: ProductSubGroup) {
    if (this.isSchoolPostPayAllowed()) return true;
    return this.schoolCredits?.some(credits => credits.product.mappedId === subGroup.currency || subGroup.currency == 'casa');
  }

  isFreeProduct(subgroup: ProductSubGroup) {
    return subgroup.currency == 'free' || subgroup.name != 'eBooks'
  }

  openSubscriptionModal(subGroup: ProductSubGroup) {
    if(this._listName == "teacher"
      && !this._isTeacherSubscribed
      && this.isSubscriptionGroup(subGroup))
      this.openModalEvent.emit()
  }

  hasSubscriptionForProducts(subGroup: ProductSubGroup) {
    if(this._listName == "student" || !this.isSubscriptionGroup(subGroup)) return true
    return this._isTeacherSubscribed;
  }

  // Teacher eBooks paid with the teacher subscription (r3 eBooks, OCC eBooks, Teacher Guide Plus).
  private isSubscriptionGroup(subGroup: ProductSubGroup) {
    return subGroup.currency == 'subscription.BASE'
  }

  changeSubscription() {
    if (this._isTeacherSubscribed)
      this.orderLinesToCancel.push(this.simpleProduct(SupportedSubs.TeacherSub.mappedId))
    else {
      this.productsToOrder.push(this.simpleProduct(SupportedSubs.TeacherSub.mappedId))
    }
    this.postOrder().subscribe()
  }

  checkSubscriptionStatus(entries: MappedProductTransactionEntry[]) {
    const isTeacherSubscribed = entries.some(
      entry => entry.product?.mappedId === SupportedSubs.TeacherSub.mappedId
    )
    if (isTeacherSubscribed != this._isTeacherSubscribed) {
      this.isTeacherSubscribed = isTeacherSubscribed
      this.isTeacherSubscribedChange.emit(this._isTeacherSubscribed)
    }
  }
}
